import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  DollarSign, 
  Calendar,
  User,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Edit,
  Plus,
  RefreshCw
} from 'lucide-react';
import { contasReceberService } from '../services/api';
import { formatCurrency, formatDate, formatCPFCNPJ } from '../utils/formatters';

const ContasReceber = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [selectedConta, setSelectedConta] = useState(null);
  const [showBaixaModal, setShowBaixaModal] = useState(false);
  const [baixaForm, setBaixaForm] = useState({
    valor_pago: '',
    data_pagamento: '',
    forma_pagamento: 'PIX',
    obs_pagamento: '',
    liquidado_rec: 'Sim'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('vencimento_rec');
  const [sortOrder, setSortOrder] = useState('asc');

  const queryClient = useQueryClient();

  // Buscar contas a receber
  const { data: contasData, isLoading, error } = useQuery({
    queryKey: ['contas-receber', currentPage, searchTerm, statusFilter, sortBy, sortOrder],
    queryFn: () => contasReceberService.getContas({
      page: currentPage,
      search: searchTerm,
      status: statusFilter,
      ordering: sortOrder === 'desc' ? `-${sortBy}` : sortBy
    }),
    keepPreviousData: true
  });

  // Mutation para baixa de conta
  const baixaMutation = useMutation({
    mutationFn: (data) => contasReceberService.baixarConta(data.id, data.baixaData),
    onSuccess: () => {
      queryClient.invalidateQueries(['contas-receber']);
      queryClient.invalidateQueries(['dashboard']);
      setShowBaixaModal(false);
      setSelectedConta(null);
      setBaixaForm({
        valor_pago: '',
        data_pagamento: '',
        forma_pagamento: 'PIX',
        obs_pagamento: '',
        liquidado_rec: 'Sim'
      });
    },
    onError: (error) => {
      console.error('Erro na baixa:', error);
      alert('Erro ao realizar baixa da conta');
    }
  });

  // Filtrar contas baseado no status
  const getStatusIcon = (liquidado) => {
    switch (liquidado) {
      case 'Sim':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Par':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'Nao':
        return <Clock className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = (liquidado) => {
    switch (liquidado) {
      case 'Sim':
        return 'Liquidada';
      case 'Par':
        return 'Parcial';
      case 'Nao':
        return 'Em Aberto';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusColor = (liquidado) => {
    switch (liquidado) {
      case 'Sim':
        return 'bg-green-100 text-green-800';
      case 'Par':
        return 'bg-yellow-100 text-yellow-800';
      case 'Nao':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleBaixa = (conta) => {
    setSelectedConta(conta);
    setBaixaForm({
      valor_pago: conta.valor_rec || '',
      data_pagamento: new Date().toISOString().split('T')[0],
      forma_pagamento: 'PIX',
      obs_pagamento: '',
      liquidado_rec: 'Sim'
    });
    setShowBaixaModal(true);
  };

  const submitBaixa = () => {
    if (!selectedConta) return;

    const baixaData = {
      valor_pago: baixaForm.valor_pago,
      data_pagamento: baixaForm.data_pagamento,
      forma_pagamento: baixaForm.forma_pagamento,
      obs_pagamento: baixaForm.obs_pagamento,
      liquidado_rec: baixaForm.liquidado_rec
    };

    baixaMutation.mutate({
      id: selectedConta.id_conta_rec,
      baixaData
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const formasPagamento = [
    'PIX', 'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito', 
    'Boleto', 'Transferência', 'Ted', 'Cheque', 'Depósito Identificado'
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Carregando contas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <span className="ml-2 text-red-600">Erro ao carregar contas</span>
      </div>
    );
  }

  const contas = contasData?.results || [];
  const totalPages = contasData?.total_pages || 1;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Contas a Receber</h1>
        <p className="text-gray-600">Gerencie e realize baixas das contas a receber</p>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Busca */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por cliente, documento, observações..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtro de Status */}
          <div className="lg:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="todos">Todos os Status</option>
              <option value="Nao">Em Aberto</option>
              <option value="Par">Parcial</option>
              <option value="Sim">Liquidada</option>
            </select>
          </div>

          {/* Botão de Atualizar */}
          <button
            onClick={() => queryClient.invalidateQueries(['contas-receber'])}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-red-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Em Aberto</p>
              <p className="text-lg font-semibold text-gray-900">
                {contas.filter(c => c.liquidado_rec === 'Nao').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Parcial</p>
              <p className="text-lg font-semibold text-gray-900">
                {contas.filter(c => c.liquidado_rec === 'Par').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Liquidada</p>
              <p className="text-lg font-semibold text-gray-900">
                {contas.filter(c => c.liquidado_rec === 'Sim').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(contas.reduce((sum, c) => sum + parseFloat(c.valor_rec || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Contas */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('nome_cliente')}
                >
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    Cliente
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('n_documento_rec')}
                >
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-1" />
                    Documento
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('vencimento_rec')}
                >
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Vencimento
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('valor_rec')}
                >
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Valor
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('valor_pago')}
                >
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Pago
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contas.map((conta) => (
                <tr key={conta.id_conta_rec} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {conta.nome_cliente || 'Cliente não informado'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {conta.id_cliente}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm text-gray-900">
                        {conta.n_documento_rec || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {conta.observacoes_rec?.substring(0, 30) || ''}
                        {conta.observacoes_rec?.length > 30 ? '...' : ''}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(conta.vencimento_rec)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(conta.data_emissao)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(conta.valor_rec)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCurrency(conta.valor_pago)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(conta.liquidado_rec)}`}>
                      {getStatusIcon(conta.liquidado_rec)}
                      <span className="ml-1">{getStatusText(conta.liquidado_rec)}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleBaixa(conta)}
                        disabled={conta.liquidado_rec === 'Sim'}
                        className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                          conta.liquidado_rec === 'Sim'
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Baixa
                      </button>
                      <button
                        onClick={() => setSelectedConta(conta)}
                        className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Página <span className="font-medium">{currentPage}</span> de{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Baixa */}
      {showBaixaModal && selectedConta && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Baixa de Conta - {selectedConta.nome_cliente}
              </h3>
              
              <div className="space-y-4">
                {/* Informações da Conta */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-medium">Documento:</span>
                      <p>{selectedConta.n_documento_rec || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Vencimento:</span>
                      <p>{formatDate(selectedConta.vencimento_rec)}</p>
                    </div>
                    <div>
                      <span className="font-medium">Valor Original:</span>
                      <p className="font-semibold">{formatCurrency(selectedConta.valor_rec)}</p>
                    </div>
                    <div>
                      <span className="font-medium">Valor Pago:</span>
                      <p className="font-semibold">{formatCurrency(selectedConta.valor_pago)}</p>
                    </div>
                  </div>
                </div>

                {/* Formulário de Baixa */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor Pago *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={baixaForm.valor_pago}
                      onChange={(e) => setBaixaForm({...baixaForm, valor_pago: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data do Pagamento *
                    </label>
                    <input
                      type="date"
                      value={baixaForm.data_pagamento}
                      onChange={(e) => setBaixaForm({...baixaForm, data_pagamento: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Forma de Pagamento *
                    </label>
                    <select
                      value={baixaForm.forma_pagamento}
                      onChange={(e) => setBaixaForm({...baixaForm, forma_pagamento: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {formasPagamento.map((forma) => (
                        <option key={forma} value={forma}>{forma}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observações
                    </label>
                    <textarea
                      value={baixaForm.obs_pagamento}
                      onChange={(e) => setBaixaForm({...baixaForm, obs_pagamento: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Observações sobre o pagamento..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status da Liquidação
                    </label>
                    <select
                      value={baixaForm.liquidado_rec}
                      onChange={(e) => setBaixaForm({...baixaForm, liquidado_rec: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="Sim">Liquidada</option>
                      <option value="Par">Parcial</option>
                      <option value="Nao">Em Aberto</option>
                    </select>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowBaixaModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={submitBaixa}
                    disabled={baixaMutation.isLoading || !baixaForm.valor_pago || !baixaForm.data_pagamento}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {baixaMutation.isLoading ? 'Processando...' : 'Confirmar Baixa'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {selectedConta && !showBaixaModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Detalhes da Conta
                </h3>
                <button
                  onClick={() => setSelectedConta(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium">Cliente:</span>
                  <p>{selectedConta.nome_cliente}</p>
                </div>
                <div>
                  <span className="font-medium">Documento:</span>
                  <p>{selectedConta.n_documento_rec || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium">Emissão:</span>
                  <p>{formatDate(selectedConta.data_emissao)}</p>
                </div>
                <div>
                  <span className="font-medium">Vencimento:</span>
                  <p>{formatDate(selectedConta.vencimento_rec)}</p>
                </div>
                <div>
                  <span className="font-medium">Valor:</span>
                  <p className="font-semibold">{formatCurrency(selectedConta.valor_rec)}</p>
                </div>
                <div>
                  <span className="font-medium">Valor Pago:</span>
                  <p className="font-semibold">{formatCurrency(selectedConta.valor_pago)}</p>
                </div>
                <div>
                  <span className="font-medium">Status:</span>
                  <p>{getStatusText(selectedConta.liquidado_rec)}</p>
                </div>
                <div>
                  <span className="font-medium">Observações:</span>
                  <p>{selectedConta.observacoes_rec || 'Nenhuma'}</p>
                </div>
              </div>

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={() => {
                    setSelectedConta(null);
                    handleBaixa(selectedConta);
                  }}
                  disabled={selectedConta.liquidado_rec === 'Sim'}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium ${
                    selectedConta.liquidado_rec === 'Sim'
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  Realizar Baixa
                </button>
                <button
                  onClick={() => setSelectedConta(null)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContasReceber; 